// src/hooks/usePixQrCode.js
import { useState, useEffect, useCallback } from "react";

const usePixQrCode = () => {
    const [pixKey, setPixKey] = useState("");
    const [amount, setAmount] = useState("1.00");
    const [description, setDescription] = useState("");
    const [pixType, setPixType] = useState("cpf");
    const [allowUserToEnterAmount, setAllowUserToEnterAmount] = useState(false);
    const [beneficiaryName, setBeneficiaryName] = useState("");
    const [beneficiaryCity, setBeneficiaryCity] = useState("");
    const [qrData, setQrData] = useState("");

    const updateQrData = useCallback(() => {
        const payload = generatePixPayload({ pixKey, amount, description, pixType, beneficiaryName, beneficiaryCity });
        setQrData(payload);
    }, [pixKey, amount, description, pixType, beneficiaryName, beneficiaryCity]);

    useEffect(() => {
        updateQrData();
    }, [pixKey, amount, description, pixType, beneficiaryName, beneficiaryCity, updateQrData]);

    const handlePixKeyChange = (e) => {
        setPixKey(e.target.value);
    };

    const handleAmountChange = (e) => {
        setAmount(e.target.value);
    };

    const handleDescriptionChange = (e) => {
        setDescription(e.target.value);
    };

    const handlePixTypeChange = (e) => {
        setPixType(e.target.value);
    };

    const handleAllowUserToEnterAmountChange = () => {
        setAllowUserToEnterAmount(!allowUserToEnterAmount);
    };

    const handleBeneficiaryNameChange = (e) => {
        setBeneficiaryName(e.target.value);
    };

    const handleBeneficiaryCityChange = (e) => {
        setBeneficiaryCity(e.target.value);
    };

    return {
        pixKey,
        amount,
        description,
        pixType,
        allowUserToEnterAmount,
        qrData,
        handlePixKeyChange,
        handleAmountChange,
        handleDescriptionChange,
        handlePixTypeChange,
        handleAllowUserToEnterAmountChange,
        handleBeneficiaryNameChange,
        handleBeneficiaryCityChange,
        beneficiaryName,
        beneficiaryCity
    };
};

const generatePixPayload = ({ pixKey, amount, description, pixType, beneficiaryName, beneficiaryCity }) => {
    amount = validateAndFormatAmount(amount);
    const gui = emvTag("00", "BR.GOV.BCB.PIX");
    const key = emvTag("01", formatPixKey(pixKey, pixType));
    const txIdTag = emvTag("05", description || "***");
    const accountInfo = emvTag("26", gui + key);
    const merchantCategoryCode = emvTag("52", "0000");
    const transactionCurrency = emvTag("53", "986");
    const transactionAmount = amount ? emvTag("54", parseFloat(amount).toFixed(2)) : "";
    const countryCode = emvTag("58", "BR");
    const name = emvTag("59", formatBeneficiaryName(beneficiaryName));
    const city = emvTag("60", formatBeneficiaryCity(beneficiaryCity));
    const additionalData = emvTag("62", formatAdittionalData(txIdTag));

    let payload = [
        emvTag("00", "01"),
        accountInfo,
        merchantCategoryCode,
        transactionCurrency,
        transactionAmount,
        countryCode,
        name,
        city,
        additionalData
    ].filter(Boolean).join("");

    const crc = crc16(payload + "6304");
    return payload + emvTag("63", crc);
};

const emvTag = (id, value) => {
    const length = value.length.toString().padStart(2, '0');
    return id + length + value;
};

const crc16 = (payload) => {
    let crc = 0xFFFF;
    for (let i = 0; i < payload.length; i++) {
        crc ^= payload.charCodeAt(i) << 8;
        for (let j = 0; j < 8; j++) {
            crc = (crc & 0x8000) ? (crc << 1) ^ 0x1021 : crc << 1;
        }
    }
    return (crc & 0xFFFF).toString(16).toUpperCase().padStart(4, '0');
};

const formatPixKey = (key, type) => {
    type = type.toUpperCase();
    if (type === 'CPF' || type === 'CNPJ') {
        return key.replace(/\D/g, ''); // Remove pontos, traços, barras
    } else if (type === 'TELEFONE') {
        return '+' + key.replace(/\D/g, ''); // Remove espaços, hífens e adiciona "+"
    }
    return key;
};

const formatBeneficiaryName = (name) => {
    const withoutAccents = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const onlyLetters = withoutAccents.replace(/[^a-zA-Z\s]/g, "");
    return onlyLetters.length > 25 ? onlyLetters.substring(0, 25) : onlyLetters;
};

const formatBeneficiaryCity = (city) => {
    const withoutAccents = city.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const onlyLetters = withoutAccents.replace(/[^a-zA-Z\s]/g, "");
    return onlyLetters.length > 15 ? onlyLetters.substring(0, 15) : onlyLetters;
};

const formatAdittionalData = (data) => {
    const withoutAccents = data.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const alphanumeric = withoutAccents.replace(/[^a-zA-Z0-9]/g, "");
    return alphanumeric.length > 20 ? alphanumeric.substring(0, 20) : alphanumeric;
};


const validateAndFormatAmount = (amount) => {
    if (!amount) return "1.50"; // Valor padrão

    // Substitui vírgulas por pontos
    let formattedAmount = amount.replace(',', '.');

    // Remove todos os pontos, exceto o último
    const parts = formattedAmount.split('.');
    if (parts.length > 2) {
        formattedAmount = parts.slice(0, -1).join('') + '.' + parts.slice(-1);
    }

    // Verifica se o valor é válido e retorna o valor formatado corretamente
    const parsedAmount = parseFloat(formattedAmount);
    if (isNaN(parsedAmount)) {
        return "1.50"; // Valor padrão se a conversão falhar
    }

    return parsedAmount.toFixed(2);
};

export default usePixQrCode;